import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { Domain } from '@monsido/modules/models/api/domain';
import { BackendDomainsRepo } from '@monsido/modules/endpoints/api/backend_admin/backend-domains.repo';
import { DomainFileParserService } from './domain-file-parser.service';
import { DomainValidatorService } from './domain-validator.service';
import { DomainCreationService } from './domain-creation.service';
import {
    CleanedDomainData,
    ImportProgress,
    ImportConfiguration,
    ImportResult,
    ImportSummary,
    ParsedImportFile,
    WizardStep,
} from '../types/domain-management.types';

@Injectable({
    providedIn: 'root',
})
export class DomainImportWizardService {
    private currentStepSubject = new BehaviorSubject<WizardStep>(WizardStep.UPLOAD);
    private progressSubject = new BehaviorSubject<ImportProgress>({ current: 0, total: 0, message: '' });
    private configurationSubject = new BehaviorSubject<ImportConfiguration>({
        skipDomainGroups: false,
        skipPathConstraints: false,
        skipLinkExcludes: false,
    });
    private parsedFileSubject = new BehaviorSubject<ParsedImportFile | null>(null);
    private importSummarySubject = new BehaviorSubject<ImportSummary | null>(null);

    public currentStep$ = this.currentStepSubject.asObservable();
    public progress$ = this.progressSubject.asObservable();
    public configuration$ = this.configurationSubject.asObservable();
    public parsedFile$ = this.parsedFileSubject.asObservable();
    public importSummary$ = this.importSummarySubject.asObservable();

    constructor (
        private backendDomainsRepo: BackendDomainsRepo,
        private domainFileParserService: DomainFileParserService,
        private domainValidatorService: DomainValidatorService,
        private domainCreationService: DomainCreationService,
    ) {}

    /**
     * Parse uploaded JSON file and validate structure
     */
    parseImportFile (file: File): Promise<ParsedImportFile> {
        return this.domainFileParserService.parseImportFile(file).then(result => {
            this.parsedFileSubject.next(result);
            return result;
        });
    }

    /**
     * Validate individual domain data
     */
    private validateDomainData (domain: unknown, index: number): string[] {
        const prefix = `Domain ${index + 1}:`;

        if (!domain || typeof domain !== 'object') {
            return [`${prefix} Invalid domain format`];
        }

        const domainData = domain as Record<string, unknown>;
        const errors = this.domainValidatorService.validateRequiredFields(domainData);

        // Add prefix to errors
        return errors.map(error => `${prefix} ${error}`);
    }

    /**
     * Update import configuration
     */
    updateConfiguration (config: Partial<ImportConfiguration>): void {
        const current = this.configurationSubject.value;
        this.configurationSubject.next({ ...current, ...config });
    }

    /**
     * Get current configuration
     */
    getConfiguration (): ImportConfiguration {
        return this.configurationSubject.value;
    }

    /**
     * Get parsed file data
     */
    getParsedFile (): ParsedImportFile | null {
        return this.parsedFileSubject.value;
    }

    /**
     * Navigate to specific wizard step
     */
    goToStep (step: WizardStep): void {
        this.currentStepSubject.next(step);
    }

    /**
     * Navigate to next step
     */
    nextStep (): void {
        const current = this.currentStepSubject.value;
        const steps = Object.values(WizardStep);
        const currentIndex = steps.indexOf(current);

        if (currentIndex < steps.length - 1) {
            this.currentStepSubject.next(steps[currentIndex + 1]);
        }
    }

    /**
     * Navigate to previous step
     */
    previousStep (): void {
        const current = this.currentStepSubject.value;
        const steps = Object.values(WizardStep);
        const currentIndex = steps.indexOf(current);

        if (currentIndex > 0) {
            this.currentStepSubject.next(steps[currentIndex - 1]);
        }
    }

    /**
     * Start the import process
     */
    async startImport (customerId: number): Promise<ImportSummary> {
        const parsedFile = this.parsedFileSubject.value;
        const config = this.configurationSubject.value;

        if (!parsedFile || !parsedFile.isValid) {
            throw new Error('No valid file data to import');
        }

        const summary: ImportSummary = {
            totalDomains: parsedFile.totalCount,
            successCount: 0,
            failureCount: 0,
            results: [],
            startedAt: new Date(),
        };

        this.importSummarySubject.next(summary);
        this.goToStep(WizardStep.PROGRESS);

        try {
            const results = await this.processDomainImport(parsedFile.domains, customerId, config);

            summary.results = results;
            summary.successCount = results.filter(r => r.success).length;
            summary.failureCount = results.filter(r => !r.success).length;
            summary.completedAt = new Date();

            this.importSummarySubject.next(summary);
            this.goToStep(WizardStep.RESULTS);

            return summary;
        } catch (error) {
            this.progressSubject.next({
                current: 0,
                total: parsedFile.totalCount,
                message: `Import failed: ${error?.message || 'Unknown error'}`,
            });
            throw error;
        }
    }

    /**
     * Process domain import with enhanced progress tracking and error handling
     */
    private async processDomainImport (
        domains: CleanedDomainData[],
        customerId: number,
        config: ImportConfiguration,
    ): Promise<ImportResult[]> {
        const results: ImportResult[] = [];
        const startTime = Date.now();
        let errorsEncountered = 0;

        this.progressSubject.next({
            current: 0,
            total: domains.length,
            message: 'Starting domain validation...',
            phase: 'validation',
            errorsEncountered: 0,
        });

        for (let i = 0; i < domains.length; i++) {
            const domain = domains[i];
            const domainDisplayName = domain.title || domain.url;
            const progress = i + 1;
            const estimatedTimeRemaining = this.calculateEstimatedTime(startTime, progress, domains.length);

            this.progressSubject.next({
                current: i,
                total: domains.length,
                message: `Processing domain ${progress} of ${domains.length}: ${domainDisplayName}`,
                currentDomain: domainDisplayName,
                phase: 'creation',
                estimatedTimeRemaining,
                errorsEncountered,
            });

            try {
                const createdDomain = await this.createDomain(domain, customerId, config);
                results.push({
                    success: true,
                    domain: domain,
                    createdDomain: createdDomain,
                });
            } catch (error) {
                errorsEncountered++;
                const errorMessage = error?.message || 'Unknown error';

                // Categorize error type
                let errorType: 'validation' | 'network' | 'server' | 'permission' | 'unknown' = 'unknown';
                let retryable = false;

                if (errorMessage.includes('Invalid URL') || errorMessage.includes('required')) {
                    errorType = 'validation';
                } else if (errorMessage.includes('permission') || errorMessage.includes('401') || errorMessage.includes('403')) {
                    errorType = 'permission';
                } else if (errorMessage.includes('500') || errorMessage.includes('503') || errorMessage.includes('timeout')) {
                    errorType = 'server';
                    retryable = true;
                } else if (errorMessage.includes('network') || errorMessage.includes('connection')) {
                    errorType = 'network';
                    retryable = true;
                }

                results.push({
                    success: false,
                    domain: domain,
                    error: errorMessage,
                    errorType,
                    retryable,
                });

                // Stop import if critical error and not configured to continue
                if (!config.continueOnError && errorType === 'permission') {
                    this.progressSubject.next({
                        current: i + 1,
                        total: domains.length,
                        message: `Import stopped due to permission error: ${errorMessage}`,
                        phase: 'completed',
                        errorsEncountered,
                    });
                    break;
                }
            }

            // Update final progress
            this.progressSubject.next({
                current: i + 1,
                total: domains.length,
                message: `Processed ${i + 1} of ${domains.length} domains`,
                currentDomain: domainDisplayName,
                phase: i + 1 === domains.length ? 'completed' : 'creation',
                estimatedTimeRemaining: i + 1 === domains.length ? 0 : estimatedTimeRemaining,
                errorsEncountered,
            });

            // Add delay between requests to avoid overwhelming the API
            if (i < domains.length - 1) {
                await new Promise(resolve => setTimeout(resolve, 200));
            }
        }

        // Final progress update
        const successful = results.filter(r => r.success).length;
        const failed = results.filter(r => !r.success).length;

        this.progressSubject.next({
            current: domains.length,
            total: domains.length,
            message: `Import completed! ${successful} domains created successfully, ${failed} failed.`,
            phase: 'completed',
            errorsEncountered,
        });

        return results;
    }

    /**
     * Calculate estimated time remaining for import
     */
    private calculateEstimatedTime (startTime: number, current: number, total: number): number {
        if (current === 0) {
            return 0;
        }

        const elapsed = Date.now() - startTime;
        const averageTimePerDomain = elapsed / current;
        const remaining = total - current;

        return Math.round(remaining * averageTimePerDomain / 1000);
    }

    /**
     * Create a single domain with configuration options
     */
    private async createDomain (
        domainData: CleanedDomainData,
        customerId: number,
        config: ImportConfiguration,
    ): Promise<Domain> {
        try {
            const creationRequest = {
                url: domainData.url,
                name: domainData.title || domainData.url,
                language: domainData.language || 'en',
                tags: domainData.labels || [],
                description: domainData.notes || '',
                createdBy: 'import',
                cms: domainData.cms,
            };

            const result = await this.domainCreationService.createDomain(customerId, creationRequest).toPromise();

            if (!result.success) {
                throw new Error(result.error || 'Domain creation failed');
            }

            return result.domain as Domain;
        } catch (error) {
            const errorMessage = error?.message || 'Unknown error occurred while creating domain';
            throw new Error(`Failed to create domain "${domainData.title || domainData.url}": ${errorMessage}`);
        }
    }

    /**
     * Generate error report for failed imports
     */
    generateErrorReport (): string {
        const summary = this.importSummarySubject.value;
        if (!summary) {
            return '';
        }

        const failedResults = summary.results.filter(r => !r.success);

        let report = `Domain Import Error Report\n`;
        report += `Generated: ${new Date().toISOString()}\n`;
        report += `Total Domains: ${summary.totalDomains}\n`;
        report += `Successful: ${summary.successCount}\n`;
        report += `Failed: ${summary.failureCount}\n\n`;

        if (failedResults.length > 0) {
            report += `Failed Domains:\n`;
            report += `=================\n\n`;

            failedResults.forEach((result, index) => {
                report += `${index + 1}. Domain: ${result.domain.title || result.domain.url}\n`;
                report += `   URL: ${result.domain.url}\n`;
                report += `   Error: ${result.error}\n\n`;
            });
        }

        return report;
    }

    /**
     * Download error report as text file
     */
    downloadErrorReport (): void {
        const report = this.generateErrorReport();
        const blob = new Blob([report], { type: 'text/plain' });
        const url = window.URL.createObjectURL(blob);

        const link = document.createElement('a');
        link.href = url;
        link.download = `domain_import_errors_${this.getTimestamp()}.txt`;
        link.style.display = 'none';

        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        window.URL.revokeObjectURL(url);
    }

    /**
     * Reset wizard state
     */
    resetWizard (): void {
        this.currentStepSubject.next(WizardStep.UPLOAD);
        this.progressSubject.next({ current: 0, total: 0, message: '' });
        this.parsedFileSubject.next(null);
        this.importSummarySubject.next(null);
        this.configurationSubject.next({
            skipDomainGroups: false,
            skipPathConstraints: false,
            skipLinkExcludes: false,
        });
    }

    /**
     * Generate timestamp for filename
     */
    private getTimestamp (): string {
        const now = new Date();
        return now.toISOString().replace(/[:.]/g, '-').slice(0, 19);
    }
}
